/**
 * "Full tracks - colour coded" display mode: same real GPS routes as
 * "Full tracks - standard" (see tracksFullStandard.js and
 * MapComponent.refreshFullTrackTraces - both modes share the exact same
 * fullTrackTraces fetch/cache, no separate fetching needed here), but
 * rendered as many short two-point segments, each coloured by the local
 * hour-of-day of its GPS fix (see utils/trackColorUtil.getHourColor for the
 * "sky gradient" formula and extractTimedCoordinates for the per-point
 * timestamp pairing).
 *
 * A dark outline layer sits underneath the coloured line, mirroring the
 * existing actualTracks_outline halo, so every hue in the gradient stays
 * legible against both the standard basemap and satellite imagery.
 */

import {
  getHourColor,
  extractTimedCoordinates
} from '../../../../utils/trackColorUtil';
import { computeWithTrackCache, pruneTrackCache } from './trackSegmentCache';

export const TRACKS_FULL_COLOUR_SOURCE_ID = 'TracksFullColour';
export const TRACKS_FULL_COLOUR_LAYER_ID = 'Tracks_lines_colour';
export const TRACKS_FULL_COLOUR_OUTLINE_LAYER_ID =
  'Tracks_lines_colour_outline';

/**
 * Above this many simultaneously active tracks, 'Full tracks - colour
 * coded' is force-switched back to 'Full tracks - standard' (see
 * MapComponent.updateTracksFullColourLayer) rather than left to degrade
 * silently.
 *
 * There is no published figure for "how many overlapping, single-legend,
 * gradient-coloured GPS tracks stay readable on a real map" - that's a
 * different problem from the ones the closest literature actually covers:
 * Miller's ~7 chunk working-memory limit and ColorBrewer's ~8-12 max
 * distinguishable categorical hues both assume matching individual items
 * to a per-item legend entry, and the "spaghetti plot" line-chart guidance
 * (~5 lines, see data-to-viz.com/caveat/spaghetti.html) assumes tracing one
 * series at a time - here every track shares the same hour-of-day
 * gradient and the task is reading an area's overall colour pattern at a
 * glance, not picking out individual tracks. The actual failure mode is
 * geographic overplotting (overlapping lines optically blend into a
 * uniform smear), which depends on tracks-per-screen-area, not on the
 * total count - a track-dense small reserve saturates far sooner than the
 * same count spread across a huge one. A fixed total-count cutoff is
 * therefore a pragmatic proxy, not a scientifically derived limit. 500 is
 * kept as a reasonable order-of-magnitude safeguard pending a proper
 * density-based measure; per-track caching (see trackSegmentCache.js)
 * means this is no longer primarily a performance limit either.
 */
export const MAX_COLOUR_CODED_TRACKS = 500;

/**
 * Adds the (initially empty) source + outline layer + coloured line layer,
 * if not already present.
 * @param {string} [visibility] - 'visible' | 'none'.
 */
export function addTracksFullColourLayer(visibility = 'none') {
  if (!this.map) return;

  if (!this.map.getSource(TRACKS_FULL_COLOUR_SOURCE_ID)) {
    this.map.addSource(TRACKS_FULL_COLOUR_SOURCE_ID, {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] }
    });
  }

  if (!this.map.getLayer(TRACKS_FULL_COLOUR_OUTLINE_LAYER_ID)) {
    this.map.addLayer({
      id: TRACKS_FULL_COLOUR_OUTLINE_LAYER_ID,
      source: TRACKS_FULL_COLOUR_SOURCE_ID,
      type: 'line',
      layout: { visibility, 'line-join': 'round', 'line-cap': 'round' },
      paint: {
        'line-color': 'rgba(0,0,0,0.55)',
        'line-width': 5,
        'line-opacity': 1
      }
    });
  }

  if (!this.map.getLayer(TRACKS_FULL_COLOUR_LAYER_ID)) {
    this.map.addLayer({
      id: TRACKS_FULL_COLOUR_LAYER_ID,
      source: TRACKS_FULL_COLOUR_SOURCE_ID,
      type: 'line',
      layout: { visibility, 'line-join': 'round', 'line-cap': 'round' },
      paint: {
        'line-color': ['coalesce', ['get', 'hourColor'], '#1f4ba8'],
        'line-width': 2.5,
        'line-opacity': 0.95
      }
    });
    this.map.moveLayer(TRACKS_FULL_COLOUR_LAYER_ID);
  }
}

/**
 * Splits one full-trace feature into per-segment 2-point LineStrings, each
 * tagged with the hour-of-day colour of its starting fix (properties.hourColor)
 * and the raw hour itself (properties.hour) - the latter is what
 * MapComponent.applyTrackHourRangeFilter() filters on for the hour-range
 * slider on the legend. The source feature's own properties are preserved
 * on every segment (hourColor/hour are added on top, not swapped in) so
 * callers that rely on other properties - e.g. actualTracks.js reusing this
 * for the default track line's hover/click metadata - don't lose them.
 * Falls back to a single untimed segment (no colour, no hour, paint falls
 * back to the default blue) if the feature has no usable per-point
 * timestamps - so a track is never silently dropped just because timing
 * data is missing, and untimed segments are always shown regardless of the
 * selected hour range.
 * @param {object} feature - a GeoJSON Feature (MultiPoint/LineString).
 * @returns {object[]} an array of coloured LineString Features.
 */
export function toColouredSegments(feature) {
  const timed = extractTimedCoordinates(feature);
  const baseProperties = (feature && feature.properties) || {};
  if (timed.length >= 2) {
    const segments = [];
    for (let index = 0; index < timed.length - 1; index += 1) {
      segments.push({
        type: 'Feature',
        geometry: {
          type: 'LineString',
          coordinates: [timed[index].coordinate, timed[index + 1].coordinate]
        },
        properties: {
          ...baseProperties,
          hourColor: getHourColor(timed[index].hour),
          hour: timed[index].hour
        }
      });
    }
    return segments;
  }

  // No per-point timestamps available - render the whole feature as one
  // untimed line rather than showing nothing.
  const { geometry } = feature || {};
  if (!geometry) return [];
  if (geometry.type === 'LineString' || geometry.type === 'MultiLineString') {
    return [{ ...feature, properties: { ...(feature.properties || {}) } }];
  }
  if (
    geometry.type === 'MultiPoint' &&
    Array.isArray(geometry.coordinates) &&
    geometry.coordinates.length >= 2
  ) {
    return [
      {
        ...feature,
        geometry: { type: 'LineString', coordinates: geometry.coordinates },
        properties: { ...(feature.properties || {}) }
      }
    ];
  }
  return [];
}

/**
 * Rebuilds the source data from the full-trace cache plus the current stub
 * features - same MapComponent.getActiveFullTraceTrackIds() pruning as
 * updateTracksFullStandardLayer (see that function's comment for why), so
 * tracks don't flash in and out as the bulk fetch's stub list shifts around
 * during panning, but also don't linger on the map forever once fetched.
 *
 * Per-track segment computation is memoised (see trackSegmentCache.js) on
 * `this._colouredSegmentsCache`, keyed by track id - a track whose driving
 * data (full trace, or stub while the trace is still loading) hasn't
 * changed since the last rebuild reuses its previously computed segments
 * instead of re-running toColouredSegments() over every point again.
 */
export function updateTracksFullColourLayer() {
  if (!this.map) return;
  const source = this.map.getSource(TRACKS_FULL_COLOUR_SOURCE_ID);
  if (!source) return;

  const stubFeatures = this.getTracksMapLayerFeatures
    ? this.getTracksMapLayerFeatures()
    : [];
  const cache = this.props.fullTrackTraces || {};

  const stubById = new Map();
  stubFeatures.forEach(feature => {
    const trackId = this.getTrackFeatureId(feature);
    if (trackId && !stubById.has(trackId)) {
      stubById.set(trackId, feature);
    }
  });

  const trackIds = this.getActiveFullTraceTrackIds
    ? this.getActiveFullTraceTrackIds()
    : new Set([...Object.keys(cache), ...stubById.keys()]);

  if (!this._colouredSegmentsCache) this._colouredSegmentsCache = new Map();
  const segmentsCache = this._colouredSegmentsCache;

  const segments = [];
  trackIds.forEach(trackId => {
    const fullResponse = cache[trackId];
    const fullResponseFeatures = Array.isArray(
      fullResponse && fullResponse.features
    )
      ? fullResponse.features
      : [];

    // Full trace not loaded (yet) - fall back to the coarse stub so the
    // track isn't invisible while it loads.
    const trackSource =
      fullResponseFeatures.length > 0 ? fullResponse : stubById.get(trackId);
    if (!trackSource) return;

    const trackSegments = computeWithTrackCache(
      segmentsCache,
      trackId,
      trackSource,
      () =>
        fullResponseFeatures.length > 0
          ? fullResponseFeatures.reduce(
              (all, feature) => all.concat(toColouredSegments(feature)),
              []
            )
          : toColouredSegments(trackSource)
    );
    segments.push(...trackSegments);
  });

  pruneTrackCache(segmentsCache, trackIds);
  source.setData({ type: 'FeatureCollection', features: segments });
}
